import aa from 'search-insights';
import {AlgoliaClient} from '@/api/algoliaclient';

const appID = process.env.VUE_APP_ALGOLIA_APP_ID
const browseGoalsIndex = process.env.VUE_APP_ALGOLIA_GOALS_INDEX

let browseGoalsClient;

async function setBrowseGoalsClient() {
  browseGoalsClient = await AlgoliaClient.create({
    appID,
    featureName: 'browse_goals',
    indices: browseGoalsIndex
  })
  // when running jest tests, NODE_ENV is set to 'test'
  if (process.env.NODE_ENV !== 'test') {
    await browseGoalsClient.initAnalytics()
  }
}

async function getBrowseGoalsClient() {
  if (!browseGoalsClient) {
    await setBrowseGoalsClient()
  }
  return browseGoalsClient
}


/*
  Performs the necessary multiple Algolia searches to get hits and enabled facets

  Returns/resolves to {
    hits: [...],
    nbHits,
    facets: {
      grades: [...],
      domains: [...],
      standards: [...]
    },
    // anything else that comes back with the results from the request with hits
  }
*/
export async function multiSearchAlgolia(browseFilters, standardSets, maxNumResults) {
  const query = browseFilters.keyword || ''
  const { hitsFacets, facetRequests } = requestsAndOptionsForBrowseFilters(browseFilters, standardSets)
  const client = await getBrowseGoalsClient()
  const promises = [client.search(query, {
    facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(browseFilters, standardSets),
    facets: hitsFacets,
    hitsPerPage: maxNumResults,
    responseFields: ['hits', 'facets', 'nbHits', 'processingTimeMS', 'query', 'params'],
    clickAnalytics: true
  })]

  facetRequests.forEach(request => {
    let filters = {}
    request.filterFields.forEach(k => filters[k] = browseFilters[k])

    const shouldIncludeQuery = request.filterFields.includes('keyword')
    promises.push(client.search(shouldIncludeQuery ? query : '', {
      facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(filters, standardSets),
      facets: request.facets,
      responseFields: ['facets']
    }))
  })

  return Promise.all(promises).then(responses => {
    let facets = {}
    const results = responses[0];
    if (results.facets) {
      facets = Object.assign(facets, results.facets)
    }

    for (let i = 1; i < responses.length; i++) {
      const response = responses[i];
      if (response.facets) {
        facets = Object.assign(facets, response.facets)
      }
    }
    results.facets = facets
    return results
  })
}

export async function getGradesWithResultsForFilters(browseFilters, standardSets) {
  const client = await getBrowseGoalsClient()
  const resp = await client.search('', {
    facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(browseFilters, standardSets),
    facets: [FACET_GRADES],
    responseFields: ['nbHits', 'facets']
  })
  const gradeFacets = resp.facets?.grades || {}
  return Object.keys(gradeFacets)
}

export const FACET_GRADES = 'grades',
  FACET_DOMAINS = 'domains',
  FACET_STANDARDS = 'standard_set_refs.standard_long_ref'

/*
  Computes what facets and filter fields should be used for Algolia requests given what browse filters are set (encoded as a string). The implementation is basically a table/matrix. See https://docs.google.com/spreadsheets/d/1teWchROdyD_YsVrLTqBNQg5sJaf4MVtmK3lX9jrNvK8/edit#gid=0

  Returns {
    hitsFacets: [...],
    facetRequests: [
      {
        filterFields: [...],
        facets: [...]
      }
    ]
  }
*/
export function requestsAndOptionsForBrowseFilters(filters) {
  const encodedFilterCondition = encodeBrowseFilters(filters)
  switch (encodedFilterCondition) {
    case '10000':
      return {
        hitsFacets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: []
      }
    case '11000':
      return {
        hitsFacets: [FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: [{
          facets: [FACET_GRADES],
          filterFields: ['subject'],
        }]
      }
    case '11100':
      return {
        hitsFacets: [FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'subjectDomain'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades'],
            facets: [FACET_DOMAINS]
          },
        ]
      }
    case '11110':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'subjectDomain'],
            facets: [FACET_STANDARDS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'standard'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades', 'standard'],
            facets: [FACET_DOMAINS]
          },
        ]
      }
    case '11101':
      return {
        hitsFacets: [FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'keyword'],
            facets: [FACET_DOMAINS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
    case '11111':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'subjectDomain', 'keyword'],
            facets: [FACET_STANDARDS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'standard', 'keyword'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades', 'standard', 'keyword'],
            facets: [FACET_DOMAINS]
          }
        ]
      }
    case '11010':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_GRADES]
          },
          {
            filterFields: ['subject', 'grades'],
            facets: [FACET_DOMAINS, FACET_STANDARDS]
          }
        ]
      }
    case '11001':
      return {
        hitsFacets: [FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
    case '11011':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'grades', 'keyword'],
            facets: [FACET_DOMAINS, FACET_STANDARDS]
          },
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
    case '10100':
      return {
        hitsFacets: [FACET_GRADES, FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_DOMAINS]
          }
        ]
      }
    case '10110':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_DOMAINS]
          },
          {
            filterFields: ['subject', 'subjectDomain'],
            facets: [FACET_GRADES, FACET_STANDARDS]
          }
        ]
      }
    case '10111':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'standard', 'keyword'],
            facets: [FACET_GRADES, FACET_DOMAINS]
          },
          {
            filterFields: ['subject', 'subjectDomain', 'keyword'],
            facets: [FACET_STANDARDS]
          }
        ]
      }
    case '10101':
      return {
        hitsFacets: [FACET_GRADES, FACET_STANDARDS],
        facetRequests: [
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_DOMAINS]
          }
        ]
      }
    case '10010':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject'],
            facets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS]
          }
        ]
      }
    case '10011':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['subject', 'keyword'],
            facets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS]
          }
        ]
      }
    case '10001':
      return {
        hitsFacets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS],
        facetRequests: []
      }
    case '00001':
      return {
        hitsFacets: [FACET_GRADES],
        facetRequests: []
      }
    case '01001':
      return {
        hitsFacets: [],
        facetRequests: [
          {
            filterFields: ['keyword'],
            facets: [FACET_GRADES]
          }
        ]
      }
    default:
      console.warn('Unexpected filter condition ' + encodedFilterCondition)
      return {
        hitsFacets: [],
        facetRequests: []
      }
  }
}

/*
This function makes the code for unit tests and computing facet requests for different combinations of filters present more manageable
 Encodes the browse filters into a binary string, where 1 indicates whether a filter is present and 0 if not
 Order of bits is: subject, grades, subjectDomain, standard, and keyword
 e.g. if only subject is set, we return 10000 (grades, subjectDomain, standard, and keyword are not set)
*/
export function encodeBrowseFilters(filters) {
  const subjectPresent = !!filters.subject,
    domainPresent = !!filters.subjectDomain,
    gradesPresent = filters.grades && filters.grades.length && true || false,
    queryPresent = !!filters.keyword,
    standardPresent = !!filters.standard

  return [subjectPresent, gradesPresent, domainPresent, standardPresent, queryPresent].map(b => b ? '1' : '0').join('')
}

/*
Generates the necessary facetFilters for an Algolia search request

filters structure:
  {
    grades: [],
    subject: '',
    subjectDomain: '',
    standard: '',
    keyword: '',
    levelOfSupport: 0,
    characteristics: []
  }
*/
export function generateAlgoliaFacetFiltersFromBrowseFilters(filters, standardSets) {
  const facetFilters = []
  if (filters.subject) {
    facetFilters.push([`subject:${filters.subject}`])
  }
  if (Array.isArray(filters.grades) && filters.grades.length) {
    const gradeFilters = []
    filters.grades.forEach(g => gradeFilters.push(`grades:${g}`))
    facetFilters.push(gradeFilters)
  }
  if (filters.subjectDomain) {
    facetFilters.push([`domains:${filters.subjectDomain}`])
  }
  if (filters.standard) {
    facetFilters.push([`standard_set_refs.standard_long_ref:${filters.standard}`])
  }
  if (Array.isArray(standardSets) && standardSets.length) {
    const standardFilters = standardSets.map(set => `standard_sets:${set}`)
    facetFilters.push(standardFilters)
  }
  if (filters.levelOfSupport >= 0) {
    facetFilters.push([`levels_of_support:${filters.levelOfSupport}`])
  }
  if (Array.isArray(filters.characteristics) && filters.characteristics.length) {
    const characteristicsFilters = []
    filters.characteristics.forEach(c => characteristicsFilters.push(`characteristics:${c}`))
    facetFilters.push(characteristicsFilters)
  }
  return facetFilters
}

/*
  Extracts facet values for grades, domains, and standards as arrays
*/
export function facetValuesWithResultsFromAlgoliaFacetResponse(facets) {
  return {
    grades: facets.grades && Object.keys(facets.grades) || null,
    domains: facets.domains && Object.keys(facets.domains) || null,
    standards: facets['standard_set_refs.standard_long_ref'] && Object.keys(facets['standard_set_refs.standard_long_ref']) || null
  }
}

/* Wrapper functions for sending algolia analytics events */
export async function sendAlgoliaClickedBrowseGoalResultEvent({userToken, queryID, objectIDs, positions}) {
  const client = await getBrowseGoalsClient()
  aa('clickedObjectIDsAfterSearch', {
    userToken: userToken || client.getPlaceholderUserToken(),
    eventName: 'Clicked Goal Tile',
    index: browseGoalsIndex,
    objectIDs,
    positions,
    queryID,
  })
}
